import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST } from '../../../../constants/common'
import { useScreen } from '../../../../hooks'
import { FormModalContainer, useCloseConfirm } from '../../../Form'
import {
  MINIMIZED_MODAL_KEY,
  MODAL_HEADER_VARIANT,
  useMinimizableModal,
  useResizeModal,
} from '../../../Modal'
import {
  LISTING_EDIT_MINIMIZED_REPLACE_CONFIRMATION,
  LISTING_MINIMIZED_REPLACE_CONFIRMATION,
  RentalPortalListingFormPopupContainer,
  useRentalPortalListingDraft,
  useRentalPortalListingDraftCloseConfirmation,
} from '../../../RentalPortalListing'
import { RentalPortalListingsForm } from '../RentalPortalListingsForm'

import {
  QA_NAME_LISTING_CREATION_MODAL,
  QA_NAME_LISTING_EDIT_MODAL,
} from './RentalPortalListingsFormModal.constants'
import {
  getIsListingFormDirty,
  getListingEditMinimizedSourceId,
} from './RentalPortalListingsFormModal.utils'

function RentalPortalListingsFormModal({
  initialValues,
  isDirtyFromOpen,
  listingId,
  onClose,
  onReload,
  ...restProps
}) {
  const { t } = useTranslation()

  const [modal, setModal] = useState(null)

  const [createdListingId, setCreatedListingId] = useState(null)

  const [uploadingFiles, setUploadingFiles] = useState(EMPTY_LIST)

  const getIsDirty = getIsListingFormDirty({
    hasUploadingFiles: uploadingFiles.length > 0,
    isDirtyFromOpen,
  })

  const { handleClose, isCloseConfirm, setCloseConfirm } = useCloseConfirm({
    getIsDirty,
    onClose: handleCloseSession,
  })

  const { isMobileScreenSize } = useScreen()

  const { fullScreen, handleResizeModal } = useResizeModal()

  const { draftId, setDraft } = useRentalPortalListingDraft()

  const draftCloseConfirmation =
    useRentalPortalListingDraftCloseConfirmation(setDraft)

  const isEditMode = Boolean(listingId)

  const titleKey = isEditMode
    ? 'RentalPortal.ListingFormEditListingTitleKey'
    : 'RentalPortal.ListingFormNewListingTitleKey'

  const qaName = isEditMode
    ? QA_NAME_LISTING_EDIT_MODAL
    : QA_NAME_LISTING_CREATION_MODAL

  const hasSavedRecord = isEditMode || Boolean(createdListingId)

  const { handleMinimize, isMinimized, setIsDirty } = useMinimizableModal({
    closeConfirmation: hasSavedRecord ? undefined : draftCloseConfirmation,
    minimizeKey: MINIMIZED_MODAL_KEY.RENTAL_PORTAL_LISTING_FORM,
    qaName,
    replaceConfirmation: hasSavedRecord
      ? LISTING_EDIT_MINIMIZED_REPLACE_CONFIRMATION
      : LISTING_MINIMIZED_REPLACE_CONFIRMATION,
    sourceId: isEditMode ? getListingEditMinimizedSourceId(listingId) : draftId,
    title: t(titleKey),
    onClose: handleCloseSession,
  })

  function handleCloseModal() {
    setModal(null)
  }

  function handleCloseSession() {
    onClose()

    if (listingId || createdListingId) {
      onReload?.()
    }
  }

  function handleCreateRecord(record) {
    setCreatedListingId(record.id)
    setDraft(null, 0)
  }

  return (
    <RentalPortalListingsForm
      {...restProps}
      FormContainer={
        isEditMode ? FormModalContainer : RentalPortalListingFormPopupContainer
      }
      containerProps={{
        confirmationProps:
          uploadingFiles.length > 0
            ? { description: 'RentalPortal.ListingFormAbandonedUploadsKey' }
            : undefined,
        files: uploadingFiles,
        getIsDirty,
        hasSavedRecord,
        headerVariant: isEditMode
          ? MODAL_HEADER_VARIANT.EDIT
          : MODAL_HEADER_VARIANT.CREATE,
        hidden: isMinimized,
        isCloseConfirm,
        isFullScreen: isMobileScreenSize || fullScreen,
        modal,
        qaName,
        setCloseConfirm,
        title: isEditMode ? t(titleKey) : titleKey,
        onClose: handleCloseSession,
        onIsDirtyChange: setIsDirty,
        onMinimize: handleMinimize,
        onResize: isMobileScreenSize ? undefined : handleResizeModal,
      }}
      contentComponentProps={{ isFullScreen: isMobileScreenSize || fullScreen }}
      createdListingId={createdListingId}
      initialValues={initialValues}
      listingId={listingId}
      setModal={setModal}
      uploadingFiles={uploadingFiles}
      onCancel={handleClose}
      onCloseModal={handleCloseModal}
      onCreateRecord={handleCreateRecord}
      onUploadingFilesChange={setUploadingFiles}
    />
  )
}

export default RentalPortalListingsFormModal
